/**
 * Patterns Slide
 *
 * Slide 3: "Top patterns in your industry" - Shows detected patterns.
 */

import { motion } from 'framer-motion'
import { Lightbulb, Sparkles, Target, MessageSquare, Play, Star } from 'lucide-react'
import type { OnboardingArtifacts } from '../../../contexts/OnboardingContext'

interface PatternsSlideProps {
  artifacts: OnboardingArtifacts
}

const PATTERN_ICONS = [
  { icon: Target, color: 'text-rose-400', bg: 'bg-rose-500/10' },
  { icon: MessageSquare, color: 'text-blue-400', bg: 'bg-blue-500/10' },
  { icon: Play, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  { icon: Star, color: 'text-amber-400', bg: 'bg-amber-500/10' },
  { icon: Sparkles, color: 'text-purple-400', bg: 'bg-purple-500/10' },
]

export default function PatternsSlide({ artifacts }: PatternsSlideProps) {
  // Handle multiple possible artifact structures
  const patterns = artifacts.patterns as {
    top_hooks?: string[]
    top_ctas?: string[]
    common_patterns?: string[]
    winning_formats?: string[]
    brand_patterns?: {
      hook_patterns?: string[]
      proof_patterns?: string[]
      offer_patterns?: string[]
      cta_patterns?: string[]
      dominant_tone?: string
      key_benefits?: string[]
    }
  } | null

  // Try to get patterns from brand_patterns first, then fall back to top-level
  const brandPatterns = patterns?.brand_patterns

  // Combine different pattern types
  const allPatterns: string[] = []
  if (brandPatterns?.hook_patterns) allPatterns.push(...brandPatterns.hook_patterns.slice(0, 2))
  if (brandPatterns?.proof_patterns) allPatterns.push(...brandPatterns.proof_patterns.slice(0, 2))
  if (brandPatterns?.offer_patterns) allPatterns.push(...brandPatterns.offer_patterns.slice(0, 1))
  if (brandPatterns?.cta_patterns) allPatterns.push(...brandPatterns.cta_patterns.slice(0, 1))
  if (patterns?.top_hooks) allPatterns.push(...patterns.top_hooks.slice(0, 2))
  if (patterns?.top_ctas) allPatterns.push(...patterns.top_ctas.slice(0, 2))
  if (patterns?.common_patterns) allPatterns.push(...patterns.common_patterns.slice(0, 2))
  if (patterns?.winning_formats) allPatterns.push(...patterns.winning_formats.slice(0, 2))

  // Default patterns if none found
  const displayPatterns = allPatterns.length > 0
    ? allPatterns.slice(0, 5)
    : [
        'Problem-Solution Hook',
        'Social Proof Opening',
        'Limited Time Urgency',
        'Before/After Reveal',
        'UGC Testimonial',
      ]

  return (
    <div className="flex flex-col items-center text-center">
      {/* Icon */}
      <motion.div
        initial={{ scale: 0, rotate: -180 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ delay: 0.2, type: 'spring', duration: 0.8 }}
        className="w-20 h-20 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center mb-8 shadow-lg"
      >
        <Lightbulb className="w-10 h-10 text-white" />
      </motion.div>

      {/* Title */}
      <motion.h2
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="text-3xl font-bold text-tf-text mb-2"
      >
        Winning Patterns Detected
      </motion.h2>

      <motion.p
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="text-tf-text-muted mb-10 max-w-md"
      >
        These hooks and formats are crushing it in your industry right now
      </motion.p>

      {/* Pattern badges - appear one by one */}
      <div className="flex flex-wrap justify-center gap-3 max-w-lg">
        {displayPatterns.map((pattern, index) => {
          const style = PATTERN_ICONS[index % PATTERN_ICONS.length]
          const Icon = style.icon

          return (
            <motion.div
              key={index}
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{
                delay: 0.5 + index * 0.15,
                type: 'spring',
                stiffness: 200,
              }}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl ${style.bg} border border-white/5`}
            >
              <Icon className={`w-4 h-4 ${style.color}`} />
              <span className={`text-sm font-medium ${style.color}`}>{pattern}</span>
            </motion.div>
          )
        })}
      </div>

      {/* Insight text */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 1.3 }}
        className="mt-10 flex items-center gap-2 text-sm text-tf-text-muted"
      >
        <Sparkles className="w-4 h-4 text-amber-400" />
        <span>We'll incorporate these patterns into your ad</span>
      </motion.div>
    </div>
  )
}
